"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "daskapital-ds";
import DisplayImage from "@/components/common/DisplayImage/DisplayImage";
import { KlaviyoNewsletter } from "@/components/common/KlaviyoNewsletter";
import { PopupConfig } from "./Popup.types";
import { parsePopupDescription } from "./Popup.utils";
import { CloseButton, StyledDialog, StyledOverlay } from "./Popup.theme";

const STORAGE_PREFIX = "dk_popup_seen_";
const DAY_MS = 24 * 60 * 60 * 1000;

/** Per-popup storage key so each popup tracks its own seen-state. */
function storageKey(popup: PopupConfig): string {
  return STORAGE_PREFIX + (popup.id ?? "default");
}

/** Whether the popup should be hidden based on the configured frequency. */
function isSuppressed(popup: PopupConfig): boolean {
  if (typeof window === "undefined") return true;

  const frequency = popup.frequency || "session";
  const key = storageKey(popup);

  if (frequency === "always") return false;

  if (frequency === "session") {
    return window.sessionStorage.getItem(key) === "1";
  }

  const raw = window.localStorage.getItem(key);
  if (!raw) return false;

  if (frequency === "once") return true;

  if (frequency === "days") {
    const seenAt = parseInt(raw, 10);
    if (Number.isNaN(seenAt)) return false;
    const days = popup.frequencyDays && popup.frequencyDays > 0 ? popup.frequencyDays : 7;
    return Date.now() - seenAt < days * DAY_MS;
  }

  return false;
}

/** Persist that the popup has been shown, honouring the configured frequency. */
function markSeen(popup: PopupConfig): void {
  if (typeof window === "undefined") return;

  const frequency = popup.frequency || "session";
  const key = storageKey(popup);

  if (frequency === "always") return;

  if (frequency === "session") {
    window.sessionStorage.setItem(key, "1");
  } else {
    window.localStorage.setItem(key, String(Date.now()));
  }
}

export default function PopupInstance({ popup }: { popup: PopupConfig }) {
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);

  const show = useCallback(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    markSeen(popup);
    setOpen(true);
  }, [popup]);

  const close = useCallback(() => setOpen(false), []);

  // Trigger engine — the method in which the popup appears.
  useEffect(() => {
    if (isSuppressed(popup)) return;

    const trigger = popup.trigger || "load";

    if (trigger === "load") {
      const timer = setTimeout(show, 800);
      return () => clearTimeout(timer);
    }

    if (trigger === "delay") {
      const seconds = popup.triggerDelay && popup.triggerDelay > 0 ? popup.triggerDelay : 3;
      const timer = setTimeout(show, seconds * 1000);
      return () => clearTimeout(timer);
    }

    if (trigger === "scroll") {
      const threshold =
        popup.triggerScroll && popup.triggerScroll > 0 ? popup.triggerScroll : 50;
      const onScroll = () => {
        const scrollable =
          document.documentElement.scrollHeight - window.innerHeight;
        const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
        if (percent >= threshold) {
          show();
          window.removeEventListener("scroll", onScroll);
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    if (trigger === "exit_intent") {
      const onMouseOut = (event: MouseEvent) => {
        if (!event.relatedTarget && event.clientY <= 0) {
          show();
          document.removeEventListener("mouseout", onMouseOut);
        }
      };
      document.addEventListener("mouseout", onMouseOut);
      return () => document.removeEventListener("mouseout", onMouseOut);
    }
  }, [popup, show]);

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  const newsletter = popup.newsletter || {};
  const button = popup.button || {};

  return (
    <StyledOverlay
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={popup.title || "Popup"}
    >
      <StyledDialog
        $bg={popup.backgroundColor}
        $color={popup.textColor}
        onClick={(event) => event.stopPropagation()}
      >
        <CloseButton
          type="button"
          onClick={close}
          aria-label="Close"
          $color={popup.textColor}
        >
          &times;
        </CloseButton>

        {popup.image && (
          <div className="popup__image">
            <DisplayImage
              url={popup.image}
              alt={popup.title || ""}
              fill
              sizes="(max-width: 768px) 100vw, 40vw"
            />
          </div>
        )}

        <div className="popup__content">
          {popup.title && <h2 className="popup__title">{popup.title}</h2>}
          {popup.description &&
            parsePopupDescription(popup.description, "popup__description")}

          <div className="popup__action">
            {popup.showNewsletter ? (
              <KlaviyoNewsletter
                variant="inline"
                buttonVariant={popup.buttonVariant || "primary"}
                listId={newsletter.klaviyoListId || undefined}
                placeholder={newsletter.placeholder || undefined}
                buttonText={newsletter.buttonText || undefined}
                successMessage={newsletter.successMessage || undefined}
                showNameFields={newsletter.showNameFields || false}
                label=""
              />
            ) : (
              button.text && (
                <Button
                  variant={popup.buttonVariant || "primary"}
                  href={button.url || undefined}
                  target={button.newTab ? "_blank" : undefined}
                  rel={button.newTab ? "noreferrer noopener" : undefined}
                >
                  {button.text}
                </Button>
              )
            )}
          </div>
        </div>
      </StyledDialog>
    </StyledOverlay>
  );
}
